import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';

(function () {
  const cfg = window.charmBuilderConfig || {};
  const blockId = cfg.blockId;
  const sectionId = cfg.sectionId;
  const productFormId = 'product-form-' + sectionId;
  const MAX_CHARMS = 10;
  const CHARM_SIZE = 2.32;
  // Chain LOCAL space anchors, index = slot. Captured via ?anchor=1.
  const CHARM_ANCHORS = [
    { x: -20.44509485637755, y: 1.1, z: -2.2115181980581773 },
    { x: -20.44706218177102, y: 1.3085394921726057, z: -4.117607985155717 },
    { x: -20.23116200742532, y: 1.8150827292255587, z: -6.140145079164745 },
    { x: -19.87605437289703, y: 2.6000979974142755, z: -8.061115618675611 },
    { x: -19.28227412346515, y: 3.74362901826401, z: -9.966774462261002 },
    { x: -21.04228524110024, y: 1.0376845419005605, z: 2.1141278367798537 },
    { x: -20.79380866532005, y: 1.3146422661990929, z: 4.224863626865155 },
    { x: -20.284446451542212, y: 1.8991343204823254, z: 6.245104217776474 },
    { x: -19.92153455879306, y: 2.617043474566406, z: 8.1039886708572 },
    { x: -19.247711016067075, y: 3.497029669345812, z: 10.047215849131392 },
  ];

  const canvas = document.getElementById('threejs-cube-' + blockId);
  const loader_el = document.getElementById('threejs-cube-loader-' + blockId);
  const galleryEl = document.getElementById('charm-gallery-' + blockId);
  const trayEl = document.getElementById('charm-tray-' + blockId);
  const counterEl = document.getElementById('charm-counter-' + blockId);

  const mainUrl = cfg.mainUrl || '';
  const defaultCharmUrl = cfg.defaultCharmUrl || '';
  const charms = cfg.charms || [];

  if (!mainUrl) { loader_el.textContent = 'No model URL set.'; return; }

  // ---------- Three.js scene ----------
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: false, preserveDrawingBuffer: true });
  const initialSize = canvas.clientWidth || 500;
  renderer.setSize(initialSize, initialSize, false);
  renderer.setPixelRatio(window.devicePixelRatio);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 0.92;
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0xffffff);
  // match namechain viewer: PMREM room env so metal has something to reflect
  // (without this, high-metalness silver renders dark/flat)
  const pmrem = new THREE.PMREMGenerator(renderer);
  scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
  scene.environmentIntensity = 1.0;

  const camera = new THREE.PerspectiveCamera(25, 1, 0.01, 100);
  camera.position.set(0, 0, 2.5);

  const controls = new OrbitControls(camera, canvas);
  controls.enableDamping = true;
  controls.dampingFactor = 0.08;
  controls.autoRotateSpeed = 1.5;
  controls.enableZoom = false;
  controls.enablePan = false;
  controls.target.set(0, 0, 0);
  controls.enabled = false;
  // env map now does most lighting; direct lights softened to avoid blowout
  scene.add(new THREE.AmbientLight(0xffffff, 0.3));
  const key = new THREE.DirectionalLight(0xffffff, 2);
  key.position.set(3, 4, 3);
  scene.add(key);
  const fill = new THREE.DirectionalLight(0xfff0e0, 1);
  fill.position.set(-3, 1, -2);
  scene.add(fill);
  const back = new THREE.DirectionalLight(0xffffff, 0.8);
  back.position.set(0, 2, -4);
  scene.add(back);
  // point light to brighten charms + chain
  const pt = new THREE.PointLight(0xffffff, 85.6);
  pt.position.set(0, 5, 8);
  scene.add(pt);

  const _validColorSpaces = new Set(['', 'srgb', 'srgb-linear', 'display-p3', 'display-p3-linear']);
  function fixUVChannels(model) {
    model.traverse((child) => {
      if (!child.isMesh) return;
      if (child.geometry && child.geometry.index) {
        const idx = child.geometry.index;
        const pos = child.geometry.attributes.position;
        let maxIdx = 0;
        for (let i = 0; i < idx.array.length; i++) { if (idx.array[i] > maxIdx) maxIdx = idx.array[i]; }
        if (pos && idx.array && maxIdx >= pos.count) {
          child.visible = false;
          return;
        }
      }
      const mats = Array.isArray(child.material) ? child.material : [child.material];
      mats.forEach(mat => {
        Object.keys(mat).forEach(key => {
          const val = mat[key];
          if (val && val.isTexture) {
            if (val.channel == null) val.channel = 0;
            if (!_validColorSpaces.has(val.colorSpace)) {
              val.colorSpace = THREE.SRGBColorSpace;
            }
          }
        });
        mat.needsUpdate = true;
      });
    });
  }

  function normalizeModel(model, targetSize) {
    const box = new THREE.Box3().setFromObject(model);
    const s = box.getSize(new THREE.Vector3());
    const c = box.getCenter(new THREE.Vector3());
    model.position.sub(c);
    model.scale.setScalar(targetSize / Math.max(s.x, s.y, s.z));
  }

  function hasImageMap(model) {
    let found = false;
    model.traverse((child) => {
      if (!child.isMesh || found) return;
      const mats = Array.isArray(child.material) ? child.material : [child.material];
      mats.forEach(mat => { if (mat && mat.map) found = true; });
    });
    return found;
  }

  function makeGoldMat() {
    return new THREE.MeshStandardMaterial({ color: 0xffcc44, metalness: 0.2, roughness: 0.28, envMapIntensity: 0.01 });
  }
  function makeSilverMat() {
    // lower metalness so it catches direct light (diffuse), higher env + brighter
    // base so silver reads bright instead of dark
    return new THREE.MeshStandardMaterial({ color: 0xf2f3f5, metalness: 0.8, roughness: 0.3, envMapIntensity: 0.5 });
  }
  function applyMetal(model, metal) {
    const make = metal === 'silver' ? makeSilverMat : makeGoldMat;
    model.traverse((child) => {
      if (!child.isMesh) return;
      if (Array.isArray(child.material)) {
        child.material = child.material.map(() => make());
      } else if (child.material) {
        child.material = make();
      }
    });
  }
  function goldenModel(model) { applyMetal(model, 'gold'); }

  // ---------- Shopify metal (Gold/Silver) → chain color ----------
  let chainModelRef = null, chainGoldenable = false;
  let defaultCharmRef = null, defaultCharmGoldenable = false;
  let currentChainMetal = ''; // '' so first read always applies
  function metalFromText(t) {
    t = (t || '').toLowerCase();
    if (t.includes('silver')) return 'silver';
    if (t.includes('gold')) return 'gold';
    return '';
  }
  // Variant pickers live in <variant-selects>/<variant-radios> OUTSIDE the form
  // (linked via form= attr). Query by section id, not the form.
  function readShopifyMetal() {
    const roots = [
      document.getElementById('variant-radios-' + sectionId),
      document.getElementById('variant-selects-' + sectionId),
      document.getElementById(productFormId),
    ].filter(Boolean);
    let txt = '';
    roots.forEach((root) => {
      root.querySelectorAll('input[type="radio"]:checked').forEach((el) => {
        const m = metalFromText(el.value); if (m) txt = m;
      });
      root.querySelectorAll('select').forEach((s) => {
        const m = metalFromText(s.value); if (m) txt = m;
      });
    });
    if (txt) return txt;
    // Fallback: resolve selected variant id → variant options
    const idInput = document.querySelector('#' + productFormId + ' input[name="id"], #' + productFormId + ' select[name="id"]');
    const vid = idInput && String(idInput.value);
    const root = document.getElementById('variant-selects-' + sectionId) || document.getElementById('variant-radios-' + sectionId);
    const json = root && root.querySelector('script[type="application/json"]');
    if (vid && json) {
      try {
        const variants = JSON.parse(json.textContent);
        const v = variants.find((x) => String(x.id) === vid);
        if (v) return metalFromText((v.title || '') + ' ' + (v.options || []).join(' '));
      } catch (e) { }
    }
    return '';
  }
  function recolorChainFromShopify() {
    const metal = readShopifyMetal();
    if (!metal || metal === currentChainMetal) return;
    currentChainMetal = metal;
    if (chainModelRef && chainGoldenable) applyMetal(chainModelRef, metal);
    if (defaultCharmRef && defaultCharmGoldenable) applyMetal(defaultCharmRef, metal);
  }
  function boostTexturedModel(model) {
    model.traverse((child) => {
      if (!child.isMesh) return;
      const mats = Array.isArray(child.material) ? child.material : [child.material];
      mats.forEach(mat => {
        if (!mat || !mat.map) return;
        mat.emissiveMap = mat.map;
        mat.emissive = new THREE.Color(1, 1, 1);
        mat.emissiveIntensity = 0.35;
        mat.needsUpdate = true;
      });
    });
  }

  // Per-charm Y-axis rotation overrides (keyed by Shopify product handle)
  const CHARM_Y_ROTATION = {
    'big-dog-energy-charm': Math.PI,
    'she-blooms-charm': 0,
    'she-s-flirty-charm': -Math.PI / 2,
    'bear-with-her-charm': -Math.PI / 2,
  };
  // Charms that keep their baked GLB texture. Gold = original texture, Silver = grayscaled at runtime.
  const TEXTURE_GRAYSCALE_CHARMS = new Set([
    'she-s-flirty-charm',
    'she-s-the-one-charm',
    'she-s-a-masterpiece-charm',
    'pearl-of-her-love-charm',
  ]);
  // Charms whose baked texture renders washed out — boost contrast + saturation.
  const TEXTURE_PUNCH_CHARMS = new Set([
    'she-s-flirty-charm',
  ]);
  const PUNCH_CONTRAST = 1.35;   // >1 = more contrast (push darks down, lights up)
  const PUNCH_SATURATION = 1.4;  // >1 = more saturated
  const grayscaleTexCache = new Map(); // sourceImage -> CanvasTexture
  const punchTexCache = new Map(); // sourceImage -> CanvasTexture
  function isDrawable(img) {
    if (!img) return false;
    if (typeof HTMLImageElement !== 'undefined' && img instanceof HTMLImageElement) return img.complete && img.naturalWidth > 0;
    if (typeof HTMLCanvasElement !== 'undefined' && img instanceof HTMLCanvasElement) return true;
    if (typeof ImageBitmap !== 'undefined' && img instanceof ImageBitmap) return true;
    if (typeof OffscreenCanvas !== 'undefined' && img instanceof OffscreenCanvas) return true;
    return false;
  }
  function getGrayscaleTexture(srcTex) {
    if (!srcTex || !srcTex.image) return null;
    const img = srcTex.image;
    if (grayscaleTexCache.has(img)) return grayscaleTexCache.get(img);
    if (!isDrawable(img)) { console.warn('[gray] not drawable', img && img.constructor && img.constructor.name); return null; }
    const w = img.width || img.naturalWidth || 256;
    const h = img.height || img.naturalHeight || 256;
    const c = document.createElement('canvas');
    c.width = w; c.height = h;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    try { ctx.drawImage(img, 0, 0, w, h); } catch (e) { console.warn('[gray] drawImage failed', e); return null; }
    // Manual luminance — works on every browser (Safari ignores ctx.filter silently).
    let id;
    try { id = ctx.getImageData(0, 0, w, h); }
    catch (e) { console.warn('[gray] getImageData failed (CORS taint?)', e); return null; }
    const d = id.data;
    for (let i = 0; i < d.length; i += 4) {
      const g = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
      d[i] = d[i + 1] = d[i + 2] = g;
    }
    ctx.putImageData(id, 0, 0);
    const t = new THREE.CanvasTexture(c);
    t.colorSpace = srcTex.colorSpace || THREE.SRGBColorSpace;
    t.wrapS = srcTex.wrapS; t.wrapT = srcTex.wrapT;
    t.minFilter = srcTex.minFilter;
    t.magFilter = srcTex.magFilter;
    t.flipY = srcTex.flipY;
    t.generateMipmaps = srcTex.generateMipmaps;
    t.anisotropy = srcTex.anisotropy;
    t.needsUpdate = true;
    grayscaleTexCache.set(img, t);
    return t;
  }
  function applyGrayscaleMap(inst, label) {
    let meshes = 0, mapsFound = 0, swapped = 0, noMap = 0;
    const swap = (mat) => {
      if (!mat) return mat;
      if (!mat.map) { noMap++; return mat; }
      mapsFound++;
      const gtx = getGrayscaleTexture(mat.map);
      if (!gtx) return mat;
      const cloned = mat.clone();
      cloned.map = gtx;
      cloned.color = new THREE.Color(0xffffff);
      cloned.needsUpdate = true;
      swapped++;
      return cloned;
    };
    inst.traverse((c) => {
      if (!c.isMesh) return;
      meshes++;
      if (Array.isArray(c.material)) c.material = c.material.map(swap);
      else c.material = swap(c.material);
    });
    console.log('[gray]', label || '', 'meshes=' + meshes, 'mapsFound=' + mapsFound, 'swapped=' + swapped, 'noMap=' + noMap);
  }

  function getPunchTexture(srcTex) {
    if (!srcTex || !srcTex.image) return null;
    const img = srcTex.image;
    if (punchTexCache.has(img)) return punchTexCache.get(img);
    if (!isDrawable(img)) { console.warn('[punch] not drawable', img && img.constructor && img.constructor.name); return null; }
    const w = img.width || img.naturalWidth || 256;
    const h = img.height || img.naturalHeight || 256;
    const c = document.createElement('canvas');
    c.width = w; c.height = h;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    try { ctx.drawImage(img, 0, 0, w, h); } catch (e) { console.warn('[punch] drawImage failed', e); return null; }
    let id;
    try { id = ctx.getImageData(0, 0, w, h); }
    catch (e) { console.warn('[punch] getImageData failed (CORS taint?)', e); return null; }
    const d = id.data;
    const C = PUNCH_CONTRAST, S = PUNCH_SATURATION;
    for (let i = 0; i < d.length; i += 4) {
      let r = d[i], g = d[i + 1], b = d[i + 2];
      // contrast around mid-gray
      r = (r - 128) * C + 128;
      g = (g - 128) * C + 128;
      b = (b - 128) * C + 128;
      // saturation: mix toward luminance
      const lum = 0.299 * r + 0.587 * g + 0.114 * b;
      r = lum + (r - lum) * S;
      g = lum + (g - lum) * S;
      b = lum + (b - lum) * S;
      d[i] = r < 0 ? 0 : r > 255 ? 255 : r;
      d[i + 1] = g < 0 ? 0 : g > 255 ? 255 : g;
      d[i + 2] = b < 0 ? 0 : b > 255 ? 255 : b;
    }
    ctx.putImageData(id, 0, 0);
    const t = new THREE.CanvasTexture(c);
    t.colorSpace = srcTex.colorSpace || THREE.SRGBColorSpace;
    t.wrapS = srcTex.wrapS; t.wrapT = srcTex.wrapT;
    t.minFilter = srcTex.minFilter;
    t.magFilter = srcTex.magFilter;
    t.flipY = srcTex.flipY;
    t.generateMipmaps = srcTex.generateMipmaps;
    t.anisotropy = srcTex.anisotropy;
    t.needsUpdate = true;
    punchTexCache.set(img, t);
    return t;
  }
  // Swap base map to contrast-boosted version + kill emissive wash from boostTexturedModel.
  function applyPunchMap(inst, label) {
    const swap = (mat) => {
      if (!mat || !mat.map) return mat;
      const ptx = getPunchTexture(mat.map);
      if (!ptx) return mat;
      const cloned = mat.clone();
      cloned.map = ptx;
      if (cloned.emissiveMap) { cloned.emissiveMap = ptx; cloned.emissiveIntensity = 0.12; }
      // matte it down — less light-reactive, kill shiny specular blowout
      cloned.metalness = 0.0;
      cloned.roughness = 0.85;
      if ('envMapIntensity' in cloned) cloned.envMapIntensity = 0.15;
      cloned.needsUpdate = true;
      return cloned;
    };
    inst.traverse((c) => {
      if (!c.isMesh) return;
      if (Array.isArray(c.material)) c.material = c.material.map(swap);
      else c.material = swap(c.material);
    });
  }

  const gltf_loader = new GLTFLoader();
  const charmCache = new Map(); // url -> Promise<{ scene, goldenable }>
  function loadGLB(url) {
    if (charmCache.has(url)) return charmCache.get(url);
    const p = new Promise((resolve, reject) => {
      gltf_loader.load(url, (gltf) => {
        fixUVChannels(gltf.scene);
        const goldenable = !hasImageMap(gltf.scene);
        if (!goldenable) boostTexturedModel(gltf.scene);
        resolve({ scene: gltf.scene, goldenable });
      }, undefined, (err) => { console.error('charm load', url, err); reject(err); });
    });
    charmCache.set(url, p);
    return p;
  }

  // Charms group — parented to chain after load, rebuilt on every change
  let charmGroup = null;
  let chainBox = null; // world-space, retained for any future use

  let buildToken = 0;
  async function rebuildCharms(selection) {
    const myToken = ++buildToken;
    if (!charmGroup) return;
    while (charmGroup.children.length) charmGroup.remove(charmGroup.children[0]);
    if (!selection || !selection.some(Boolean)) return;
    if (selection.length > CHARM_ANCHORS.length) {
      console.warn('charm selection exceeds CHARM_ANCHORS length; extras ignored');
    }

    const entries = await Promise.all(selection.map(s => s ? loadGLB(s.url).catch(() => null) : Promise.resolve(null)));
    if (myToken !== buildToken) return; // stale

    entries.forEach((entry, i) => {
      if (!entry) return;
      const a = CHARM_ANCHORS[i];
      if (!a) return;
      const sel = selection[i];
      if (!sel) return;
      const color = sel.color || 'gold';
      const inst = entry.scene.clone(true);
      const isTexCharm = TEXTURE_GRAYSCALE_CHARMS.has(sel.handle);
      console.log('[charm]', 'handle=' + sel.handle, 'color=' + color, 'isTextureGrayscale=' + isTexCharm, 'goldenable=' + entry.goldenable);
      if (isTexCharm) {
        if (color === 'silver') applyGrayscaleMap(inst, sel.handle);
        else if (TEXTURE_PUNCH_CHARMS.has(sel.handle)) applyPunchMap(inst, sel.handle); // gold + washed → boost
        // other gold = keep original texture
      } else if (entry.goldenable) {
        applyMetal(inst, color);
      }
      normalizeModel(inst, CHARM_SIZE);
      inst.position.set(a.x, a.y - 1, a.z);
      const yRot = CHARM_Y_ROTATION[sel.handle] ?? Math.PI / 2;
      inst.rotation.set(0, yRot, 0);
      charmGroup.add(inst);
    });
  }

  // ---------- Load chain + default charm ----------
  let pending = 1 + (defaultCharmUrl ? 1 : 0);
  function pendingDone() { pending--; if (pending <= 0) loader_el.style.display = 'none'; }

  gltf_loader.load(mainUrl, (gltf) => {
    const model = gltf.scene;
    fixUVChannels(model);
    const chainTextured = hasImageMap(model);
    if (chainTextured) boostTexturedModel(model);
    else goldenModel(model);
    chainModelRef = model;
    chainGoldenable = !chainTextured;
    recolorChainFromShopify(); // pick up Silver if already selected
    normalizeModel(model, 2.0);
    model.position.set(0, 0.0, -0.8);
    model.rotation.set(0, Math.PI / 2, 0);
    scene.add(model);
    model.updateMatrixWorld(true);
    chainBox = new THREE.Box3().setFromObject(model);
    charmGroup = new THREE.Group();
    model.add(charmGroup);
    rebuildCharms(state.selection);
    pendingDone();

    // === Anchor capture tool (?anchor=1) ===
    // Dev-only: drag 10 markers in chain LOCAL space; export JSON for later hardcoding.
    const ANCHOR_CAPTURE = new URLSearchParams(location.search).has('anchor');
    if (ANCHOR_CAPTURE) {
      const ANCHOR_COUNT = 10;
      const LS_KEY = 'charm-anchors-v1';
      const chainModel = model;
      const chainScale = chainModel.scale.x || 1;

      function defaultLocal(i) {
        const a = CHARM_ANCHORS[i];
        return a ? { x: a.x, y: a.y, z: a.z } : { x: 0, y: 0, z: 0 };
      }

      let saved = null;
      try { saved = JSON.parse(localStorage.getItem(LS_KEY) || 'null'); } catch (e) { }

      function makeLabel(text) {
        const c = document.createElement('canvas');
        c.width = 64; c.height = 64;
        const ctx = c.getContext('2d');
        ctx.fillStyle = 'rgba(0,0,0,0.7)';
        ctx.beginPath(); ctx.arc(32, 32, 28, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 32px sans-serif';
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(text, 32, 34);
        const tex = new THREE.CanvasTexture(c);
        tex.colorSpace = THREE.SRGBColorSpace;
        const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, depthTest: false, transparent: true }));
        const sLocal = 0.08 / chainScale;
        sp.scale.set(sLocal, sLocal, 1);
        sp.position.y = 0.06 / chainScale;
        sp.renderOrder = 1000;
        return sp;
      }

      const markerGeo = new THREE.SphereGeometry(0.025 / chainScale, 16, 16);
      const anchors = [];
      function makeMarker(i) {
        const mat = new THREE.MeshBasicMaterial({ color: 0xff3355, depthTest: false, transparent: true, opacity: 0.92 });
        const mesh = new THREE.Mesh(markerGeo, mat);
        mesh.renderOrder = 999;
        mesh.userData.anchorIndex = i;
        mesh.userData.isAnchor = true;
        mesh.add(makeLabel(String(i + 1)));
        chainModel.add(mesh);
        return mesh;
      }
      for (let i = 0; i < ANCHOR_COUNT; i++) {
        const mesh = makeMarker(i);
        let p;
        if (saved && saved[i] && Number.isFinite(saved[i].x)) p = saved[i];
        else { const d = defaultLocal(i); p = { x: d.x, y: d.y, z: d.z }; }
        mesh.position.set(p.x, p.y, p.z);
        anchors.push(mesh);
      }

      let selectedIdx = 0;
      function setSelected(idx) {
        selectedIdx = idx;
        anchors.forEach((m, k) => {
          m.material.color.set(k === idx ? 0x33ff66 : 0xff3355);
          m.scale.setScalar(k === idx ? 1.8 : 1);
        });
        syncInputs();
      }

      // --- GUI ---
      const viewerEl = canvas.parentElement;
      const panel = document.createElement('div');
      panel.style.cssText = 'position:absolute;top:8px;right:8px;background:rgba(20,20,20,0.88);color:#fff;font:12px/1.3 system-ui,sans-serif;padding:8px;border-radius:6px;z-index:10;display:flex;flex-direction:column;gap:6px;width:190px;box-shadow:0 2px 8px rgba(0,0,0,0.3);';
      panel.innerHTML =
        '<div style="font-weight:600;">Anchor capture</div>' +
        '<select data-r="sel" style="width:100%;padding:3px;background:#222;color:#fff;border:1px solid #444;">' +
        anchors.map((_, i) => '<option value="' + i + '">Anchor ' + (i + 1) + '</option>').join('') +
        '</select>' +
        '<label style="display:flex;align-items:center;gap:4px;">X<input data-r="x" type="number" step="0.001" style="flex:1;min-width:0;background:#222;color:#fff;border:1px solid #444;padding:2px;"></label>' +
        '<label style="display:flex;align-items:center;gap:4px;">Y<input data-r="y" type="number" step="0.001" style="flex:1;min-width:0;background:#222;color:#fff;border:1px solid #444;padding:2px;"></label>' +
        '<label style="display:flex;align-items:center;gap:4px;">Z<input data-r="z" type="number" step="0.001" style="flex:1;min-width:0;background:#222;color:#fff;border:1px solid #444;padding:2px;"></label>' +
        '<div style="display:flex;gap:4px;">' +
        '<button data-r="copy" type="button" style="flex:1;padding:4px;background:#2a5;color:#fff;border:0;border-radius:3px;cursor:pointer;">Copy</button>' +
        '<button data-r="dl" type="button" style="flex:1;padding:4px;background:#25a;color:#fff;border:0;border-radius:3px;cursor:pointer;">Save</button>' +
        '<button data-r="reset" type="button" style="flex:1;padding:4px;background:#a52;color:#fff;border:0;border-radius:3px;cursor:pointer;">Reset</button>' +
        '</div>' +
        '<div style="font-size:10px;opacity:0.7;">Shift=Y only · Alt=X only</div>';
      viewerEl.appendChild(panel);

      const selEl = panel.querySelector('[data-r=sel]');
      const xEl = panel.querySelector('[data-r=x]');
      const yEl = panel.querySelector('[data-r=y]');
      const zEl = panel.querySelector('[data-r=z]');

      function syncInputs() {
        const m = anchors[selectedIdx];
        selEl.value = String(selectedIdx);
        if (document.activeElement !== xEl) xEl.value = m.position.x.toFixed(4);
        if (document.activeElement !== yEl) yEl.value = m.position.y.toFixed(4);
        if (document.activeElement !== zEl) zEl.value = m.position.z.toFixed(4);
      }
      function exportData() {
        return anchors.map((m, i) => ({ i: i, x: m.position.x, y: m.position.y, z: m.position.z }));
      }
      function persist() {
        const data = exportData();
        try { localStorage.setItem(LS_KEY, JSON.stringify(data)); } catch (e) { }
        return data;
      }

      selEl.addEventListener('change', () => setSelected(parseInt(selEl.value, 10)));
      function onInput() {
        const m = anchors[selectedIdx];
        const nx = parseFloat(xEl.value), ny = parseFloat(yEl.value), nz = parseFloat(zEl.value);
        m.position.set(Number.isFinite(nx) ? nx : m.position.x, Number.isFinite(ny) ? ny : m.position.y, Number.isFinite(nz) ? nz : m.position.z);
        persist();
      }
      xEl.addEventListener('input', onInput);
      yEl.addEventListener('input', onInput);
      zEl.addEventListener('input', onInput);

      panel.querySelector('[data-r=copy]').addEventListener('click', () => {
        const json = JSON.stringify(persist());
        if (navigator.clipboard) navigator.clipboard.writeText(json).catch(() => { });
      });
      panel.querySelector('[data-r=dl]').addEventListener('click', () => {
        const blob = new Blob([JSON.stringify(persist(), null, 2)], { type: 'application/json' });
        const u = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = u; a.download = 'anchors.json';
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(u);
      });
      panel.querySelector('[data-r=reset]').addEventListener('click', () => {
        anchors.forEach((m, i) => { const d = defaultLocal(i); m.position.set(d.x, d.y, d.z); });
        persist();
        syncInputs();
      });

      setSelected(0);

      // --- Drag ---
      const raycaster = new THREE.Raycaster();
      const pointer = new THREE.Vector2();
      const dragPlane = new THREE.Plane();
      const planeNormal = new THREE.Vector3();
      const worldHit = new THREE.Vector3();
      const tmpHit = new THREE.Vector3();
      let dragging = null;
      let dragStartLocal = null;
      let downX = 0, downY = 0, moved = false;
      let modKey = null;

      function setPointer(ev) {
        const rect = renderer.domElement.getBoundingClientRect();
        pointer.x = ((ev.clientX - rect.left) / rect.width) * 2 - 1;
        pointer.y = -((ev.clientY - rect.top) / rect.height) * 2 + 1;
      }
      function pick() {
        raycaster.setFromCamera(pointer, camera);
        const hits = raycaster.intersectObjects(anchors, false);
        return hits.length ? hits[0].object : null;
      }
      renderer.domElement.addEventListener('pointerdown', (ev) => {
        setPointer(ev);
        const hit = pick();
        if (!hit) return;
        dragging = hit;
        downX = ev.clientX; downY = ev.clientY; moved = false;
        modKey = ev.shiftKey ? 'shift' : (ev.altKey ? 'alt' : null);
        dragStartLocal = dragging.position.clone();
        controls.enabled = false;
        camera.getWorldDirection(planeNormal);
        dragging.getWorldPosition(worldHit);
        dragPlane.setFromNormalAndCoplanarPoint(planeNormal, worldHit);
        try { renderer.domElement.setPointerCapture(ev.pointerId); } catch (e) { }
        ev.preventDefault();
      });
      renderer.domElement.addEventListener('pointermove', (ev) => {
        if (!dragging) return;
        if (Math.hypot(ev.clientX - downX, ev.clientY - downY) > 3) moved = true;
        setPointer(ev);
        raycaster.setFromCamera(pointer, camera);
        if (!raycaster.ray.intersectPlane(dragPlane, tmpHit)) return;
        const local = chainModel.worldToLocal(tmpHit.clone());
        if (modKey === 'shift') dragging.position.set(dragStartLocal.x, local.y, dragStartLocal.z);
        else if (modKey === 'alt') dragging.position.set(local.x, dragStartLocal.y, dragStartLocal.z);
        else dragging.position.copy(local);
        if (anchors[selectedIdx] !== dragging) setSelected(dragging.userData.anchorIndex);
        else syncInputs();
        persist();
      });
      function endDrag(ev) {
        if (!dragging) return;
        if (!moved) setSelected(dragging.userData.anchorIndex);
        try { renderer.domElement.releasePointerCapture(ev.pointerId); } catch (e) { }
        dragging = null;
        dragStartLocal = null;
        moved = false;
        modKey = null;
        controls.enabled = true;
      }
      renderer.domElement.addEventListener('pointerup', endDrag);
      renderer.domElement.addEventListener('pointercancel', endDrag);
    }
  }, undefined, (err) => { console.error('main', err); pendingDone(); });

  if (defaultCharmUrl) {
    gltf_loader.load(defaultCharmUrl, (gltf) => {
      const model = gltf.scene;
      fixUVChannels(model);
      normalizeModel(model, 0.175);
      const dcTextured = hasImageMap(model);
      if (dcTextured) boostTexturedModel(model);
      else goldenModel(model);
      defaultCharmRef = model;
      defaultCharmGoldenable = !dcTextured;
      if (defaultCharmGoldenable && currentChainMetal) applyMetal(model, currentChainMetal);
      model.position.set(0, -0.12, 0.2);
      scene.add(model);
      pendingDone();
    }, undefined, (err) => { console.error('default charm', err); pendingDone(); });
  }

  // Resize handling
  const ro = new ResizeObserver(() => {
    const w = canvas.clientWidth || 500;
    renderer.setSize(w, w, false);
  });
  ro.observe(canvas);

  (function animate() {
    requestAnimationFrame(animate);
    controls.update();
    renderer.render(scene, camera);
  })();

  // ---------- Picker / tray state ----------
  // Sparse fixed-size array. Each slot 0..9 holds either a charm or null.
  // Slot index drives anchor position; user picks slot via dropdown.
  const state = { selection: new Array(MAX_CHARMS).fill(null) };
  let uidSeq = 0;

  function slotLabel(i) { return (i < 5 ? 'L' : 'R') + (i < 5 ? i + 1 : i - 4); }
  function filledCount() { return state.selection.reduce((n, s) => s ? n + 1 : n, 0); }

  function notifyChange() {
    renderTray();
    updateCounter();
    updateGalleryDisabled();
    updateCartProperties();
    rebuildCharms(state.selection);
  }

  function addCharm(charm) {
    const empty = state.selection.indexOf(null);
    if (empty === -1) return;
    state.selection[empty] = Object.assign({ uid: ++uidSeq, color: 'gold' }, charm);
    notifyChange();
  }
  function setColorAt(index, color) {
    const item = state.selection[index];
    if (!item) return;
    if (item.color === color) return;
    item.color = color;
    notifyChange();
  }
  function removeAt(index) {
    if (!state.selection[index]) return;
    state.selection[index] = null;
    notifyChange();
  }
  function swapSlots(from, to) {
    if (from === to || from < 0 || to < 0 || from >= MAX_CHARMS || to >= MAX_CHARMS) return;
    const a = state.selection[from];
    const b = state.selection[to];
    state.selection[from] = b;
    state.selection[to] = a;
    notifyChange();
  }

  // ---------- Gallery render ----------
  function renderGallery() {
    galleryEl.innerHTML = '';
    if (charms.length === 0) {
      galleryEl.innerHTML = '<p style="grid-column:1/-1;font-size:0.8rem;color:#888;padding:0.5rem;">No charms available.</p>';
      return;
    }
    charms.forEach((charm) => {
      const tile = document.createElement('button');
      tile.type = 'button';
      tile.className = 'charm-builder__tile';
      tile.dataset.charmId = charm.id;
      tile.innerHTML =
        '<img class="charm-builder__tile-img" loading="lazy" src="' + (charm.image || '') + '" alt="' + (charm.title || '').replace(/"/g, '&quot;') + '">' +
        '<span class="charm-builder__tile-name">' + (charm.title || '') + '</span>' +
        '<span class="charm-builder__tile-price">' + (charm.price || '') + '</span>';
      tile.addEventListener('click', () => addCharm(charm));
      galleryEl.appendChild(tile);
    });
  }
  function updateGalleryDisabled() {
    const full = filledCount() >= MAX_CHARMS;
    galleryEl.querySelectorAll('.charm-builder__tile').forEach((b) => { b.disabled = full; });
  }
  function updateCounter() {
    const n = filledCount();
    counterEl.textContent = n + '/' + MAX_CHARMS;
    counterEl.dataset.full = n >= MAX_CHARMS ? 'true' : 'false';
  }

  // ---------- Tray render + drag ----------
  let dragFromIdx = -1;
  function renderTray() {
    trayEl.innerHTML = '';
    if (filledCount() === 0) {
      const li = document.createElement('li');
      li.className = 'charm-builder__tray-empty';
      li.textContent = 'Tap a charm below to add it. Use the slot dropdown to set its position.';
      trayEl.appendChild(li);
      return;
    }
    state.selection.forEach((item, i) => {
      if (!item) return;
      const li = document.createElement('li');
      li.className = 'charm-builder__tray-item';
      li.draggable = true;
      li.dataset.index = String(i);
      li.title = item.title + ' (' + (item.color === 'silver' ? 'Silver' : 'Gold') + ') – ' + slotLabel(i);
      const c = item.color || 'gold';
      const slotOptions = state.selection.map((other, j) => {
        const isSelf = j === i;
        const taken = other && !isSelf;
        const suffix = isSelf ? '' : (taken ? ' (swap with ' + (other.title || 'charm').replace(/"/g, '&quot;') + ')' : ' (empty)');
        return '<option value="' + j + '"' + (isSelf ? ' selected' : '') + '>' + slotLabel(j) + suffix + '</option>';
      }).join('');
      li.innerHTML =
        '<img class="charm-builder__tray-img" src="' + (item.image || '') + '" alt="' + (item.title || '').replace(/"/g, '&quot;') + '">' +
        '<select class="charm-builder__tray-slot" aria-label="Charm position">' + slotOptions + '</select>' +
        '<div class="charm-builder__tray-swatches" role="group" aria-label="Charm color">' +
        '<button type="button" class="charm-builder__swatch is-gold' + (c === 'gold' ? ' is-active' : '') + '" data-color="gold" aria-label="Gold" aria-pressed="' + (c === 'gold') + '"></button>' +
        '<button type="button" class="charm-builder__swatch is-silver' + (c === 'silver' ? ' is-active' : '') + '" data-color="silver" aria-label="Silver" aria-pressed="' + (c === 'silver') + '"></button>' +
        '</div>' +
        '<button type="button" class="charm-builder__tray-remove" aria-label="Remove charm">&times;</button>';
      const slotSel = li.querySelector('.charm-builder__tray-slot');
      slotSel.addEventListener('change', (e) => { e.stopPropagation(); swapSlots(i, parseInt(e.target.value, 10)); });
      slotSel.addEventListener('mousedown', (e) => e.stopPropagation());
      slotSel.addEventListener('click', (e) => e.stopPropagation());
      slotSel.addEventListener('dragstart', (e) => e.preventDefault());
      li.querySelectorAll('.charm-builder__swatch').forEach((sw) => {
        sw.addEventListener('click', (e) => {
          e.stopPropagation();
          setColorAt(i, sw.dataset.color);
        });
        sw.addEventListener('mousedown', (e) => e.stopPropagation());
        sw.addEventListener('dragstart', (e) => e.preventDefault());
      });
      li.querySelector('.charm-builder__tray-remove').addEventListener('click', (e) => {
        e.stopPropagation();
        removeAt(i);
      });
      li.addEventListener('dragstart', (e) => {
        dragFromIdx = i;
        li.classList.add('is-dragging');
        if (e.dataTransfer) { e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', String(i)); } catch (_) { } }
      });
      li.addEventListener('dragend', () => {
        li.classList.remove('is-dragging');
        trayEl.querySelectorAll('.is-over').forEach((n) => n.classList.remove('is-over'));
        dragFromIdx = -1;
      });
      li.addEventListener('dragover', (e) => {
        e.preventDefault();
        if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
        li.classList.add('is-over');
      });
      li.addEventListener('dragleave', () => li.classList.remove('is-over'));
      li.addEventListener('drop', (e) => {
        e.preventDefault();
        li.classList.remove('is-over');
        if (dragFromIdx >= 0) swapSlots(dragFromIdx, i);
      });
      trayEl.appendChild(li);
    });
  }

  // ---------- Cart hand-off ----------
  function getProductForm() {
    return document.getElementById(productFormId);
  }
  function updateCartProperties() {
    const form = getProductForm();
    if (!form) return;
    let input = form.querySelector('input[name="properties[_charms]"]');
    let titlesInput = form.querySelector('input[name="properties[Charms]"]');
    const filled = state.selection
      .map((s, i) => (s ? { s: s, i: i } : null))
      .filter(Boolean);
    if (filled.length === 0) {
      if (input) input.remove();
      if (titlesInput) titlesInput.remove();
      return;
    }
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[_charms]';
      form.appendChild(input);
    }
    if (!titlesInput) {
      titlesInput = document.createElement('input');
      titlesInput.type = 'hidden';
      titlesInput.name = 'properties[Charms]';
      form.appendChild(titlesInput);
    }
    input.value = filled.map(({ s }) => {
      if (Array.isArray(s.variants) && s.variants.length) {
        const m = s.variants.find(v => v.color === (s.color || 'gold'));
        if (m && m.id) return m.id;
      }
      return s.variantId || s.id;
    }).join(',');
    titlesInput.value = filled.map(({ s, i }) => {
      const col = s.color === 'silver' ? 'Silver' : 'Gold';
      return slotLabel(i) + ': ' + s.title + ' (' + col + ')';
    }).join(' | ');
  }

  // ---------- Screenshot → cart attribute ----------
  function captureCharmScreenshot(cb) {
    try {
      const src = renderer.domElement;
      const off = document.createElement('canvas');
      off.width = 200; off.height = 200;
      const ctx = off.getContext('2d');
      ctx.drawImage(src, 0, 0, 200, 200);
      cb(off.toDataURL('image/jpeg', 0.45));
    } catch (e) { cb(null); }
  }
  // Intercept form submit → multi-item add (chain + selected charms in one bundle)
  (function attachSubmitIntercept() {
    const form = getProductForm();
    if (!form) return;

    function readFormProps() {
      const props = {};
      form.querySelectorAll('input[name^="properties["], textarea[name^="properties["], select[name^="properties["]').forEach((el) => {
        const m = el.name.match(/^properties\[(.+)\]$/);
        if (!m) return;
        if ((el.type === 'checkbox' || el.type === 'radio') && !el.checked) return;
        props[m[1]] = el.value;
      });
      return props;
    }

    function chainItemFrom(formProps, bundleId, layout) {
      const variantInput = form.querySelector('input[name="id"]');
      const qtyInput = form.querySelector('input[name="quantity"]');
      const variantId = variantInput && variantInput.value;
      const quantity = Math.max(1, parseInt((qtyInput && qtyInput.value) || '1', 10));
      const properties = Object.assign({}, formProps);
      delete properties['_charms'];
      properties['_bundle_id'] = bundleId;
      properties['_bundle_role'] = 'chain';
      if (layout) properties['Charms'] = layout;
      return { id: variantId, quantity, properties };
    }

    function variantIdForColor(charm, color) {
      if (charm && Array.isArray(charm.variants) && charm.variants.length) {
        const match = charm.variants.find(v => v.color === color);
        if (match && match.id) return match.id;
      }
      return charm && charm.variantId;
    }
    function charmItemsFor(bundleId, paired) {
      return state.selection.map((s, i) => {
        if (!s) return null;
        const vid = variantIdForColor(s, s.color || 'gold');
        if (!vid) return null;
        return {
          id: vid,
          quantity: 1,
          properties: {
            '_bundle_id': bundleId,
            '_bundle_role': 'charm',
            '_position': slotLabel(i),
            '_pairedWith': paired || ''
          }
        };
      }).filter(Boolean);
    }

    function shotPromise() {
      return new Promise((resolve) => captureCharmScreenshot(resolve));
    }

    form.addEventListener('submit', async function (ev) {
      ev.preventDefault();
      ev.stopImmediatePropagation();

      const submitBtn = form.querySelector('[name="add"], button[type="submit"]');
      const origLabel = submitBtn && submitBtn.querySelector('span') ? submitBtn.querySelector('span').textContent : '';
      if (submitBtn) { submitBtn.setAttribute('aria-disabled', 'true'); submitBtn.classList.add('loading'); }

      const bundleId = 'chb-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 6);
      const formProps = readFormProps();
      const layout = state.selection.map((s, i) => {
        if (!s) return null;
        const col = s.color === 'silver' ? 'Silver' : 'Gold';
        return slotLabel(i) + ': ' + s.title + ' (' + col + ')';
      }).filter(Boolean).join(' | ');
      const paired = formProps['Name'] || formProps['name'] || '';

      const items = [chainItemFrom(formProps, bundleId, layout)].concat(charmItemsFor(bundleId, paired));

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items })
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || ('HTTP ' + res.status));
        }
        const shot = await shotPromise();
        if (shot || layout) {
          const attrs = {};
          if (shot) attrs['_charm_screenshot'] = shot;
          if (layout) attrs['_charm_layout'] = layout;
          try {
            await fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ attributes: attrs })
            });
          } catch (_) { }
        }
        window.location.href = '/cart';
      } catch (err) {
        alert('Could not add to cart: ' + (err && err.message || err));
        if (submitBtn) { submitBtn.removeAttribute('aria-disabled'); submitBtn.classList.remove('loading'); }
      }
    }, true);
  })();

  // ---------- Shopify color sync ----------
  // Variant pickers sit outside the form; their change events bubble to
  // document. Listen there (form listener would never fire).
  document.addEventListener('change', recolorChainFromShopify);
  document.addEventListener('variant:change', recolorChainFromShopify);

  // ---------- Init ----------
  renderGallery();
  notifyChange();
})();
